import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { authAPI } from '../services/api'
import { assetUrl } from '../config'
import toast from 'react-hot-toast'
import {
  UserCircleIcon,
  KeyIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline'

const Settings: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'account'>('profile')

  // Profile form state
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileChanged, setProfileChanged] = useState(false)

  // Profile image state
  const [profileImage, setProfileImage] = useState<File | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string>('')
  const [uploadingImage, setUploadingImage] = useState(false)

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)

  useEffect(() => {
    if (user) {
      setName(user.name)
      setEmail(user.email)
      if (user.profileImage) {
        setProfileImagePreview(assetUrl(user.profileImage) ?? '')
      }
    }
  }, [user])

  useEffect(() => {
    if (user) {
      setProfileChanged(name !== user.name || email !== user.email)
    }
  }, [name, email, user])

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error('Name is required')
      return
    }

    if (!email.trim()) {
      toast.error('Email is required')
      return
    }

    setSavingProfile(true)
    try {
      await authAPI.updateProfile({ name, email })
      toast.success('Profile updated successfully')
      setProfileChanged(false)
      // Refresh user data
      window.location.reload()
    } catch (error: any) {
      console.error('Error updating profile:', error)
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setSavingProfile(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!currentPassword) {
      toast.error('Current password is required')
      return
    }

    if (!newPassword) {
      toast.error('New password is required')
      return
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    setSavingPassword(true)
    try {
      await authAPI.changePassword({ currentPassword, newPassword })
      toast.success('Password changed successfully')
      // Clear form
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error: any) {
      console.error('Error changing password:', error)
      toast.error(error.response?.data?.error || 'Failed to change password')
    } finally {
      setSavingPassword(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB')
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, GIF, and WebP images are allowed')
      return
    }

    setProfileImage(file)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setProfileImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const uploadProfileImage = async () => {
    if (!profileImage) return

    setUploadingImage(true)
    try {
      await authAPI.uploadProfileImage(profileImage)
      toast.success('Profile image uploaded successfully')
      setProfileImage(null)
      // Refresh user data
      window.location.reload()
    } catch (error: any) {
      console.error('Error uploading profile image:', error)
      toast.error(error.response?.data?.error || 'Failed to upload profile image')
    } finally {
      setUploadingImage(false)
    }
  }

  const removeProfileImage = async () => {
    try {
      await authAPI.removeProfileImage()
      toast.success('Profile image removed successfully')
      setProfileImagePreview('')
      setProfileImage(null)
      // Refresh user data
      window.location.reload()
    } catch (error: any) {
      console.error('Error removing profile image:', error)
      toast.error(error.response?.data?.error || 'Failed to remove profile image')
    }
  }

  // Auto-upload when image is selected
  useEffect(() => {
    if (profileImage) {
      uploadProfileImage()
    }
  }, [profileImage])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'profile', name: 'Profile', icon: UserCircleIcon },
            { id: 'password', name: 'Password', icon: KeyIcon },
            { id: 'account', name: 'Account Info', icon: InformationCircleIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
              } group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <tab.icon
                className={`${
                  activeTab === tab.id ? 'text-primary' : 'text-muted-foreground/70 group-hover:text-muted-foreground'
                } -ml-0.5 mr-2 h-5 w-5`}
              />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-card shadow rounded-lg">
          <div className="px-6 py-5 border-b border-border">
            <h3 className="text-lg font-medium text-foreground">Profile Information</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Update your account's profile information and email address
            </p>
          </div>

          <form onSubmit={handleUpdateProfile} className="p-6 space-y-6">
            {/* Profile Image Upload */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Profile Image
              </label>
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  {profileImagePreview ? (
                    <img
                      src={profileImagePreview}
                      alt="Profile preview"
                      className="h-24 w-24 rounded-full object-cover border-2 border-input"
                    />
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-2xl font-bold border-2 border-input">
                      {user?.name ? getInitials(user.name) : '?'}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleProfileImageChange}
                    className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                    id="profile-image"
                    disabled={uploadingImage}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    JPG, PNG, GIF or WebP (max 2MB)
                  </p>
                  {uploadingImage && (
                    <p className="mt-2 text-sm text-primary">
                      Uploading...
                    </p>
                  )}
                  {profileImagePreview && !uploadingImage && (
                    <button
                      type="button"
                      onClick={removeProfileImage}
                      className="mt-2 text-sm text-red-600 hover:text-red-800"
                    >
                      Remove image
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                placeholder="Your name"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                placeholder="your.email@example.com"
                required
              />
              <p className="mt-1 text-xs text-muted-foreground">
                You'll need to log in again if you change your email address
              </p>
            </div>

            {profileChanged && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      You have unsaved changes. Don't forget to save!
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!profileChanged || savingProfile}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <div className="bg-card shadow rounded-lg">
          <div className="px-6 py-5 border-b border-border">
            <h3 className="text-lg font-medium text-foreground">Change Password</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Ensure your account is using a long, random password to stay secure
            </p>
          </div>

          <form onSubmit={handleChangePassword} className="p-6 space-y-6">
            <div>
              <label htmlFor="current-password" className="block text-sm font-medium text-foreground mb-2">
                Current Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  id="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="block w-full border border-input rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-ring focus:border-primary"
                  placeholder="Enter current password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground/70 hover:text-foreground"
                >
                  {showCurrentPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-foreground mb-2">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="block w-full border border-input rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-ring focus:border-primary"
                  placeholder="Enter new password"
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground/70 hover:text-foreground"
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Must be at least 6 characters long
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-foreground mb-2">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full border border-input rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-ring focus:border-primary"
                  placeholder="Confirm new password"
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground/70 hover:text-foreground"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      Passwords do not match
                    </p>
                  </div>
                </div>
              </div>
            )}

            {newPassword && newPassword.length > 0 && newPassword.length < 6 && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Password must be at least 6 characters long
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingPassword ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Account Info Tab */}
      {activeTab === 'account' && (
        <div className="bg-card shadow rounded-lg">
          <div className="px-6 py-5 border-b border-border">
            <h3 className="text-lg font-medium text-foreground">Account Information</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              View your account details and status
            </p>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">User ID</label>
                <div className="px-4 py-3 bg-muted/50 border border-border rounded-md">
                  <p className="text-sm text-foreground font-mono">{user?.id}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Role</label>
                <div className="px-4 py-3 bg-muted/50 border border-border rounded-md">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user?.role === 'admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-muted text-foreground'
                  }`}>
                    {user?.role?.toUpperCase()}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Account Created</label>
                <div className="px-4 py-3 bg-muted/50 border border-border rounded-md">
                  <p className="text-sm text-foreground">{formatDate(user?.createdAt)}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Last Login</label>
                <div className="px-4 py-3 bg-muted/50 border border-border rounded-md">
                  <p className="text-sm text-foreground">{formatDate(user?.lastLogin)}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Name</label>
                <div className="px-4 py-3 bg-muted/50 border border-border rounded-md">
                  <p className="text-sm text-foreground">{user?.name}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                <div className="px-4 py-3 bg-muted/50 border border-border rounded-md">
                  <p className="text-sm text-foreground">{user?.email}</p>
                </div>
              </div>
            </div>

            <div className="bg-primary-50 border-l-4 border-primary-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-5 w-5 text-primary-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-primary-800">Account Status: Active</h3>
                  <div className="mt-2 text-sm text-primary-700">
                    <p>Your account is in good standing and has full access to all features.</p>
                  </div>
                </div>
              </div>
            </div>

            {user?.role === 'admin' && (
              <div className="bg-purple-50 border-l-4 border-purple-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <InformationCircleIcon className="h-5 w-5 text-purple-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-purple-800">Administrator Privileges</h3>
                    <div className="mt-2 text-sm text-purple-700">
                      <p>You have full administrative access to this chatbot platform. You can:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Manage knowledge base and training data</li>
                        <li>View and respond to customer inquiries</li>
                        <li>Customize branding and appearance</li>
                        <li>Access analytics and performance metrics</li>
                        <li>Configure chatbot settings</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Settings
